import { useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Notification } from '../types/notification';
import { useTranslation } from 'react-i18next';

export function useNotifications() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { i18n } = useTranslation();

  const getUserNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userData.user.id)
        .not('title', 'eq', 'Notification Subscription')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Traiter les notifications pour appliquer les traductions si disponibles
      const processedData = data?.map(notification => {
        if (notification.translation_key && notification.translation_params) {
          try {
            // Essayer de parser les paramètres JSON si c'est une chaîne
            const params = typeof notification.translation_params === 'string' 
              ? JSON.parse(notification.translation_params) 
              : notification.translation_params;
              
            return {
              ...notification,
              title: notification.title,
              message: notification.message,
              translation_params: params
            };
          } catch (e) {
            console.error('Error parsing translation params:', e);
          }
        }
        return notification;
      });
      
      return { data: processedData as Notification[], error: null };
    } catch (err) {
      console.error('Error fetching notifications:', err);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('Error marking notification as read:', err);
      return { error: err };
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', userData.user.id)
        .eq('read', false);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      return { error: err };
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('Error deleting notification:', err);
      return { error: err };
    } finally {
      setLoading(false);
    }
  };

  const deleteAllNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Ne pas supprimer les notifications de souscription
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userData.user.id)
        .not('title', 'eq', 'Notification Subscription');

      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('Error deleting all notifications:', err);
      return { error: err };
    } finally {
      setLoading(false);
    }
  };

  interface CreateNotificationParams {
    userId: string;
    translationKey: string;
    translationParams?: Record<string, string>;
    title?: string;
    message?: string;
  }

  const createNotification = async ({
    userId,
    translationKey,
    translationParams,
    title,
    message
  }: CreateNotificationParams) => {
    try {
      setLoading(true);
      setError(null);

      // Préparer les données de notification
      const notificationData = {
        user_id: userId,
        read: false,
        translation_key: translationKey,
        translation_params: translationParams,
        title: title || i18n.t(`notifications.${translationKey}.title`, translationParams),
        message: message || i18n.t(`notifications.${translationKey}.message`, translationParams)
      };

      const { error } = await supabase
        .from('notifications')
        .insert([notificationData]);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('Error creating notification:', err);
      return { error: err };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    getUserNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    createNotification
  };
}